import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Chip from "@material-ui/core/Chip";
import InputAdornment from "@material-ui/core/InputAdornment";
import CloseIcon from "@material-ui/icons/Close";
import PersonIcon from "@material-ui/icons/Person";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import LayersIcon from "@material-ui/icons/Layers";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";

const useStyles = makeStyles((theme) => ({
  filterModalPaper: {
    borderRadius: 16,
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
    border: theme.palette.type === "dark" ? "1px solid rgba(0, 229, 255, 0.25)" : "1px solid rgba(0, 0, 0, 0.12)",
    boxShadow: theme.palette.type === "dark" ? "0 10px 40px rgba(0, 0, 0, 0.8), 0 0 20px rgba(0, 229, 255, 0.15)" : "0 10px 30px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
  },
  filterModalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    borderBottom: theme.palette.type === "dark" ? "1px solid rgba(0, 229, 255, 0.2)" : "1px solid rgba(0, 0, 0, 0.08)",
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
  },
  filterModalTitle: {
    display: "flex",
    alignItems: "center",
    fontSize: "1.15rem",
    fontWeight: 700,
    letterSpacing: "0.3px",
    color: theme.palette.type === "dark" ? "#00E5FF" : theme.palette.primary.main,
    "& svg": {
      fontSize: "1.4rem",
      marginRight: 10,
    },
  },
  filterModalContent: {
    padding: "18px 24px !important",
    backgroundColor: theme.palette.type === "dark" ? "#121418" : "#F8FAFC",
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  filterColumnBox: {
    padding: "14px 18px",
    borderRadius: 12,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.025)" : "#FFFFFF",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.07)" : "1px solid rgba(0, 0, 0, 0.08)",
    marginBottom: theme.spacing(2),
  },
  filterSectionSubheader: {
    display: "flex",
    alignItems: "center",
    fontSize: "0.82rem",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: "#00E5FF",
    marginBottom: theme.spacing(1.5),
    "& svg": {
      fontSize: "1.1rem",
      marginRight: 8,
    },
  },
  filterModalActions: {
    padding: "14px 24px",
    borderTop: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
  },
  applyButton: {
    borderRadius: 8,
    fontWeight: 700,
    fontSize: "0.88rem",
    textTransform: "none",
    padding: "8px 26px",
    backgroundColor: "#00E5FF !important",
    color: "#080A0E !important",
    boxShadow: "0 0 14px rgba(0, 229, 255, 0.4)",
    transition: "all 0.25s ease",
    "&:hover": {
      backgroundColor: "#00B4D8 !important",
      boxShadow: "0 0 18px rgba(0, 229, 255, 0.6)",
    },
  },
}));

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
  email: Yup.string().email("Invalid email").required("Required"),
});

const ModalUsers = ({ open, onClose, userId, companyId }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    email: "",
    password: "",
    profile: "user",
  };

  const { user: loggedInUser } = useContext(AuthContext);

  const [user, setUser] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      if (open) {
        try {
          const { data } = await api.get(`/users/${userId}`);
          setUser((prevState) => {
            return { ...prevState, ...data };
          });
          const userQueueIds = data.queues?.map((queue) => queue.id);
          setSelectedQueueIds(userQueueIds || []);
        } catch (err) {
          toastError(err);
        }
      }
    };

    fetchUser();
  }, [userId, open]);

  const handleClose = () => {
    onClose();
    setUser(initialState);
    setShowPassword(false);
  };

  const handleSaveUser = async (values) => {
    const userData = { ...values, companyId, queueIds: selectedQueueIds };
    try {
      if (userId) {
        await api.put(`/users/${userId}`, userData);
      } else {
        await api.post("/users", userData);
      }
      toast.success(i18n.t("userModal.success") || "Usuario guardado con éxito");
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ className: classes.filterModalPaper }}
    >
      <div className={classes.filterModalHeader}>
        <Typography className={classes.filterModalTitle}>
          <PersonIcon />
          <span>{userId ? (i18n.t("userModal.title.editUser", { name: user.name || "Usuario" }) || `Editar Usuario: ${user.name || "Usuario"}`) : (i18n.t("userModal.title.add") || "Nuevo Usuario")}</span>
          {user.profile && (
            <Chip
              label={user.profile.toUpperCase()}
              size="small"
              style={{
                marginLeft: 12,
                backgroundColor: "rgba(0, 229, 255, 0.15)",
                color: "#00E5FF",
                fontWeight: 700,
                fontSize: "0.72rem",
                border: "1px solid rgba(0, 229, 255, 0.3)",
              }}
            />
          )}
        </Typography>
        <IconButton size="small" onClick={handleClose} style={{ color: "#aaa" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <Formik
        initialValues={user}
        enableReinitialize={true}
        validationSchema={UserSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSaveUser(values);
            actions.setSubmitting(false);
          }, 400);
        }}
      >
        {({ touched, errors, isSubmitting }) => (
          <Form style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
            <DialogContent className={classes.filterModalContent}>
              {/* CAJA 1: DATOS Y CREDENCIALES */}
              <div className={classes.filterColumnBox}>
                <Typography className={classes.filterSectionSubheader}>
                  <VpnKeyIcon />
                  {i18n.t("userModal.sections.accessData") || "Datos de Acceso y Perfil"}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("userModal.form.name") || "Nombre"}
                      autoFocus
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      size="small"
                      margin="dense"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("userModal.form.password") || "Contraseña"}
                      type={showPassword ? "text" : "password"}
                      name="password"
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      variant="outlined"
                      size="small"
                      margin="dense"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setShowPassword(!showPassword)}
                              tabIndex={-1}
                              style={{ color: "#aaa" }}
                            >
                              {showPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("userModal.form.email") || "Correo Electrónico"}
                      name="email"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      variant="outlined"
                      size="small"
                      margin="dense"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <FormControl
                          variant="outlined"
                          size="small"
                          margin="dense"
                          fullWidth
                        >
                          <InputLabel id="profile-selection-input-label" shrink>
                            {i18n.t("userModal.form.profile") || "Perfil"}
                          </InputLabel>
                          <Field
                            as={Select}
                            label={i18n.t("userModal.form.profile") || "Perfil"}
                            name="profile"
                            labelId="profile-selection-label"
                            id="profile-selection"
                            notched
                            required
                          >
                            <MenuItem value="admin">Admin</MenuItem>
                            <MenuItem value="user">User</MenuItem>
                          </Field>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>
              </div>

              {/* CAJA 2: FILAS Y COLAS */}
              <Can
                role={loggedInUser.profile}
                perform="user-modal:editQueues"
                yes={() => (
                  <div className={classes.filterColumnBox} style={{ marginBottom: 0 }}>
                    <Typography className={classes.filterSectionSubheader}>
                      <LayersIcon />
                      {i18n.t("userModal.sections.assignedQueues") || "Colas y Filas Asignadas"}
                    </Typography>
                    <QueueSelect
                      selectedQueueIds={selectedQueueIds}
                      onChange={(values) => setSelectedQueueIds(values)}
                    />
                  </div>
                )}
              />
            </DialogContent>

            <DialogActions className={classes.filterModalActions}>
              <Button
                onClick={handleClose}
                variant="outlined"
                style={{
                  color: "#aaa",
                  borderColor: "rgba(255, 255, 255, 0.2)",
                  borderRadius: 8,
                  textTransform: "none",
                }}
              >
                {i18n.t("userModal.buttons.cancel") || "Cancelar"}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className={classes.applyButton}
              >
                {userId
                  ? `${i18n.t("userModal.buttons.okEdit") || "Guardar"}`
                  : `${i18n.t("userModal.buttons.okAdd") || "Crear"}`}
                {isSubmitting && (
                  <CircularProgress
                    size={18}
                    style={{ color: "#080A0E", marginLeft: 8 }}
                  />
                )}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ModalUsers;
